import { CsvItineraryRow, PortOfCall, ValidationError, ShipRecord } from '../types/itinerary';

/**
 * Apify JSON format interface for itinerary records
 */
interface ApifyItineraryRow {
  recordType?: string;
  type?: string;
  rowId?: string;
  cruiseLine: string;
  shipName: string;
  shipUrl?: string;
  shipLocation?: string;
  shipDeckplan?: string;
  sailingDate: string; // YYYY-MM-DD format
  cruiseLength: number;
  returnDate: string; // YYYY-MM-DD format
  itineraryTitle: string;
  departurePort: string;
  price?: string;
  portsOfCall: Array<{
    portName: string;
    portDate: string; // YYYY-MM-DD format
    arrivalTime?: string;
    departureTime?: string;
  }>;
  extractedAt?: string;
}

/**
 * Apify JSON format interface for ship records
 */
interface ApifyShipRow {
  recordType: 'ship';
  shipName: string;
  shipUrl?: string;
  cruiseLine: string;
  shipLocation?: string;
  shipDeckplan?: string;
  yearBuilt?: string;
  lastRefurbishment?: string;
  shipClass?: string;
  grossTonnage?: string;
  decks?: string;
  length?: string;
  width?: string;
  speed?: string;
  passengers?: string;
  crew?: string;
  cabin?: string;
  formerNames?: string;
  builder?: string;
  flagState?: string;
  extractedAt?: string;
}

/**
 * Parses a JSON file containing cruise itinerary data from Apify
 * Expected JSON structure:
 * - Array of objects
 * - Objects may have recordType: "ship" or "itinerary" (or no recordType defaults to itinerary)
 * - First object may be a summary with type: "cruise_line_summary" (should be skipped)
 * - Subsequent objects contain itinerary data with portsOfCall array or ship data
 */
export function parseItineraryJson(jsonContent: string): {
  data: CsvItineraryRow[];
  shipData: ShipRecord[];
  errors: ValidationError[];
  totalFileRows: number;
  errorRowNumbers: Set<number>;
  skippedSummaryRows: number; // Track skipped summary entries
  shipErrorRowNumbers: Set<number>; // Track ship record errors
} {
  const errors: ValidationError[] = [];
  const data: CsvItineraryRow[] = [];
  const shipData: ShipRecord[] = [];
  const errorRowNumbers = new Set<number>();
  const shipErrorRowNumbers = new Set<number>();
  let totalFileRows = 0;
  let skippedSummaryRows = 0;
  
  try {
    const jsonData = JSON.parse(jsonContent);
    
    if (!Array.isArray(jsonData)) {
      errors.push({
        row: 0,
        message: 'JSON file must contain an array of objects'
      });
      return { data, shipData, errors, totalFileRows: 0, errorRowNumbers, skippedSummaryRows: 0, shipErrorRowNumbers };
    }
    
    totalFileRows = jsonData.length;
    
    // Process each entry
    jsonData.forEach((entry: any, index: number) => {
      const rowNumber = index + 1; // 1-indexed for user display
      
      // Skip summary/metadata entries
      if (entry.type === 'cruise_line_summary') {
        console.log(`[ItineraryJsonParser] Skipping summary entry at row ${rowNumber}`);
        skippedSummaryRows++;
        return;
      }
      
      // Check if this is a ship record
      if (entry.recordType === 'ship') {
        const shipRecord = parseShipRecord(entry as ApifyShipRow, rowNumber, errors);
        if (shipRecord) {
          shipData.push(shipRecord);
        } else {
          shipErrorRowNumbers.add(rowNumber);
        }
        return;
      }
      
      // Otherwise, treat as itinerary record (default behavior for backward compatibility)
      const itineraryEntry = entry as ApifyItineraryRow;
      
      // Validate required fields
      if (!itineraryEntry.cruiseLine || !itineraryEntry.shipName || !itineraryEntry.sailingDate || !itineraryEntry.returnDate || !itineraryEntry.departurePort) {
        errors.push({
          row: rowNumber,
          recordType: 'itinerary',
          message: `Missing required fields: cruiseLine, shipName, sailingDate, returnDate, or departurePort`
        });
        errorRowNumbers.add(rowNumber);
        return;
      }
      
      // Validate date formats (should already be YYYY-MM-DD, but check anyway)
      const sailingDateValid = validateDate(itineraryEntry.sailingDate, rowNumber, 'sailingDate', errors, 'itinerary');
      const returnDateValid = validateDate(itineraryEntry.returnDate, rowNumber, 'returnDate', errors, 'itinerary');
      
      if (!sailingDateValid || !returnDateValid) {
        errorRowNumbers.add(rowNumber);
        return;
      }
      
      // Validate return date is after sailing date
      if (new Date(returnDateValid) < new Date(sailingDateValid)) {
        errors.push({
          row: rowNumber,
          recordType: 'itinerary',
          field: 'returnDate',
          message: `Return date must be after sailing date`
        });
        errorRowNumbers.add(rowNumber);
        return;
      }
      
      // Parse portsOfCall array
      const portsOfCall: PortOfCall[] = [];
      if (itineraryEntry.portsOfCall && Array.isArray(itineraryEntry.portsOfCall)) {
        itineraryEntry.portsOfCall.forEach((port, portIndex) => {
          if (!port.portName || !port.portDate) {
            errors.push({
              row: rowNumber,
              recordType: 'itinerary',
              field: `portsOfCall[${portIndex}]`,
              message: `Port at index ${portIndex} missing portName or portDate`
            });
            return;
          }
          
          // Validate port date format
          const portDateValid = validateDate(port.portDate, rowNumber, `portsOfCall[${portIndex}].portDate`, errors, 'itinerary');
          if (!portDateValid) {
            return; // Error already added by validateDate
          }
          
          // Only add port if all required fields are valid
          const portOfCall: PortOfCall = {
            name: port.portName.trim(),
            date: portDateValid
          };
          
          // Add optional fields only if they exist and are not empty
          if (port.arrivalTime && port.arrivalTime.trim()) {
            portOfCall.arrivalTime = port.arrivalTime.trim();
          }
          
          if (port.departureTime && port.departureTime.trim()) {
            portOfCall.departureTime = port.departureTime.trim();
          }
          
          portsOfCall.push(portOfCall);
        });
      }
      
      // Create CsvItineraryRow (reusing the interface name for compatibility)
      const row: CsvItineraryRow = {
        cruiseLine: itineraryEntry.cruiseLine.trim(),
        shipName: itineraryEntry.shipName.trim(),
        sailingDate: sailingDateValid,
        returnDate: returnDateValid,
        departurePort: itineraryEntry.departurePort.trim(),
        portsOfCall,
        itineraryTitle: (itineraryEntry.itineraryTitle && itineraryEntry.itineraryTitle.trim()) || '',
        cruiseLength: itineraryEntry.cruiseLength || 0,
        shipLocation: (itineraryEntry.shipLocation && itineraryEntry.shipLocation.trim()) || undefined,
        shipUrl: (itineraryEntry.shipUrl && itineraryEntry.shipUrl.trim()) || undefined,
        shipDeckplan: (itineraryEntry.shipDeckplan && itineraryEntry.shipDeckplan.trim()) || undefined
      };
      
      data.push(row);
    });
    
    console.log(`[ItineraryJsonParser] Parsed ${data.length} valid itinerary rows and ${shipData.length} ship records from ${totalFileRows} total entries (skipped ${skippedSummaryRows} summary entries)`);
    
  } catch (error) {
    errors.push({
      row: 0,
      message: `Error parsing JSON: ${error instanceof Error ? error.message : 'Unknown error'}`
    });
    return { data, shipData, errors, totalFileRows, errorRowNumbers, skippedSummaryRows, shipErrorRowNumbers };
  }
  
  return { data, shipData, errors, totalFileRows, errorRowNumbers, skippedSummaryRows, shipErrorRowNumbers };
}

/**
 * Parse a ship record from JSON
 */
function parseShipRecord(
  entry: ApifyShipRow,
  rowNumber: number,
  errors: ValidationError[]
): ShipRecord | null {
  // Validate required fields
  if (!entry.shipName || !entry.cruiseLine) {
    errors.push({
      row: rowNumber,
      recordType: 'ship',
      message: `Missing required fields: shipName or cruiseLine`
    });
    return null;
  }
  
  // Create ship record with all fields
  const shipRecord: ShipRecord = {
    recordType: 'ship',
    shipName: entry.shipName.trim(),
    cruiseLine: entry.cruiseLine.trim(),
    shipUrl: entry.shipUrl?.trim(),
    shipLocation: entry.shipLocation?.trim(),
    shipDeckplan: entry.shipDeckplan?.trim(),
    yearBuilt: entry.yearBuilt?.trim(),
    lastRefurbishment: entry.lastRefurbishment?.trim(),
    shipClass: entry.shipClass?.trim(),
    grossTonnage: entry.grossTonnage?.trim(),
    decks: entry.decks?.trim(),
    length: entry.length?.trim(),
    width: entry.width?.trim(),
    speed: entry.speed?.trim(),
    passengers: entry.passengers?.trim(),
    crew: entry.crew?.trim(),
    cabin: entry.cabin?.trim(),
    formerNames: entry.formerNames?.trim(),
    builder: entry.builder?.trim(),
    flagState: entry.flagState?.trim(),
    extractedAt: entry.extractedAt?.trim()
  };
  
  return shipRecord;
}

/**
 * Validate date format (accepts YYYY-MM-DD or MM/DD/YYYY) and convert to YYYY-MM-DD
 */
function validateDate(
  dateStr: string,
  rowNumber: number,
  fieldName: string,
  errors: ValidationError[],
  recordType: 'itinerary' | 'ship' = 'itinerary'
): string | null {
  if (!dateStr) {
    errors.push({
      row: rowNumber,
      recordType,
      field: fieldName,
      message: `Invalid date: empty value`
    });
    return null;
  }
  
  // Check if already in YYYY-MM-DD format
  const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (isoRegex.test(dateStr.trim())) {
    // Validate it's a real date
    const date = new Date(dateStr.trim());
    if (isNaN(date.getTime())) {
      errors.push({
        row: rowNumber,
        recordType,
        field: fieldName,
        message: `Invalid date: ${dateStr}`
      });
      return null;
    }
    return dateStr.trim();
  }
  
  // Try MM/DD/YYYY or M/D/YYYY format
  const convertedDate = convertDateFormat(dateStr);
  if (convertedDate) {
    return convertedDate;
  }
  
  errors.push({
    row: rowNumber,
    recordType,
    field: fieldName,
    message: `Invalid date format: ${dateStr}. Expected YYYY-MM-DD or MM/DD/YYYY`
  });
  return null;
}

/**
 * Convert date from MM/DD/YYYY or M/D/YYYY to YYYY-MM-DD format
 */
function convertDateFormat(dateStr: string): string | null {
  if (!dateStr || !dateStr.trim()) {
    return null;
  }
  
  const trimmed = dateStr.trim();
  
  // Try MM/DD/YYYY or M/D/YYYY format
  const usDateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = trimmed.match(usDateRegex);
  if (match) {
    const month = match[1].padStart(2, '0');
    const day = match[2].padStart(2, '0');
    const year = match[3];
    return `${year}-${month}-${day}`;
  }
  
  return null;
}

